import { vec2 } from "@tlaukkan/tsm";
import { Component, ComponentConstructor, Entity, SystemStateComponent, TagComponent } from "ecsy";
import * as PIXI from "pixi.js";
import { CompositionElement } from "./composition";
import { BehaviorsConstants, Chaining, CollisionCategory, CellCategory, PhysicsConstants, RenderEffects, Speeds, WeightClass, ShopConstants } from "./constants";
import { ElementStats, StatName } from "./elementDisplayInfo";
import { Game } from "./game";
import { EntityParameters } from "./gameElement";
import { GameElement } from "./gameElementTypes";
import { cellLocations } from "./generated/assetLocations";
import { HighlightType } from "./highlightType";
import { Rng } from "./rng";
import { ShapePrimative } from "./ShapePrimative";
import { Sound } from "./sound";
import { ArrayVec2 } from "./synchronization";
import { SimpleSelector, TargetFilter, TargetFilterType, TargetType, TriggeredEffect } from "./triggeredEffects";
import { AssetLocation, FrozenComponent, metersToPixels, rotateAround, Uninitialized, worldToScreenPosition } from "./utils";

export interface MergableComponent<T> extends Component {
	merge(other: Partial<T>): void
}

export class ElementInfo extends Component {
	id = -1;
	type = GameElement.SHOPABLE_ELEMENT_NUMBER;
	creatorId = -1;
	category = CellCategory.None;

	reset() {
		this.id = -1;
		this.type = GameElement.SHOPABLE_ELEMENT_NUMBER;
		this.creatorId = -1;
		this.category = CellCategory.None;
	}
}

export class Transform extends Component {
	position: vec2 = Uninitialized;
	rotation = 0;
	size = vec2.one.copy();
	scale = 1;
	/**Target direction for movement. NOT current body forward direction */
	direction: vec2 = vec2.zero.copy();
	/**Current body forward direction */
	shape: ColliderShape = ColliderShape.Circle

	//Optimization. Calculate this one on demand, so we don't have to do it every frame
	get forward() {
		return rotateAround(new vec2([0, 1]), this.rotation, vec2.zero.copy());
	}

	reset() {
		this.position = Uninitialized;
		this.rotation = 0;
		this.size = vec2.one.copy();
		this.scale = 1;
		this.direction = vec2.zero.copy();
		//this.forward = new vec2([0, 1]);
		this.shape = ColliderShape.Circle;
	}

	getRenderTransform() {
		const position = worldToScreenPosition(this.position);
		const radius = metersToPixels(this.size.x / 2);
		const scale = this.scale;
		return { position, radius, scale, rotation: -this.rotation }
	}
}

export enum ColliderShape {
	Circle,
	Box
}

// Physics component
export class Physics extends Component {
	body: B2D.b2Body = Uninitialized;
	fixture: B2D.b2Fixture = Uninitialized;
	fixtureOffset: vec2 = vec2.zero.copy();
	initialPosition: vec2 = vec2.zero.copy(); //Are these reduntent?
	initialRotation = 0;
	initialPush: vec2 | null = null;
	size = vec2.one.copy();
	shapeType = ColliderShape.Circle;
	baseSize = vec2.one.copy();
	weightClass = WeightClass.Normal;
	isHighSpeed = false;
	isSensor = false;
	mass = Uninitialized;

	reset() {
		this.body = Uninitialized;
		this.fixture = Uninitialized;
		this.fixtureOffset = vec2.zero.copy();
		this.initialPosition = vec2.zero.copy();
		this.initialRotation = 0;
		this.initialPush = null;
		this.size = vec2.one.copy();
		this.shapeType = ColliderShape.Circle;
		this.baseSize = vec2.one.copy();
		this.weightClass = WeightClass.Normal;
		this.isHighSpeed = false;
		this.isSensor = false;
		this.mass = Uninitialized;
	}
}



export class Collision extends Component {
	collidedWith: { entity: Entity, canDamage: boolean }[] = [];

	reset() {
		this.collidedWith = [];
	}
}

//Behaviors

export enum DamageType {
	HostileCollision,
	BloodCellCollision,
	Electricity,
	Explosion,
	OnlyInfect,
	AoEHarm
}

export class Attacker extends Component {
	damageType = DamageType.HostileCollision;
	damage = 1;
	kills = 0;

	reset() {
		this.damage = 1;
		this.damageType = DamageType.HostileCollision;
		this.kills = 0;
	}
}

export class Healer extends Component {
	healing = 0;

	reset() {
		this.healing = 0;
	}
}

export class Stunned extends Component {
	duration = PhysicsConstants.FriendlyCollisionStunSeconds;
	reset() {
		this.duration = PhysicsConstants.FriendlyCollisionStunSeconds;
	}
}

export class SuspendMovement extends Component {
	duration = 0;
	previousLinearV = vec2.zero.copy();
	previousAngularV = 0;
	reset() {
		this.duration = 0;
		this.previousLinearV = vec2.zero.copy();
		this.previousAngularV = 0;
	}
}

export class Slowed extends Component {
	speedReductionPercent = 1;
	reset() {
		this.speedReductionPercent = 1;
	}
}

export class Tagger extends TagComponent { }

export class TradeHealthOnHit extends TagComponent { }

export class TradeHealth extends Component {
	isPermanent = false;
	target = -1
	reset() {
		this.isPermanent = false;
		this.target = -1;
	}
}

export class Chainer extends Component {
	links = 0;
	maxLinks = 2;
	lastStepLinks = 0;
	range = Chaining.ChainRange;
	chainSprites = new Array<PIXI.Mesh>();

	reset() {
		this.links = 0;
		this.lastStepLinks = 0;
		this.maxLinks = 2;
		this.range = Chaining.ChainRange;
		this.chainSprites = new Array<PIXI.Mesh>();
	}
}

export class Chained extends Component {
	links: Entity[] = [];

	reset() {
		this.links = [];
	}
};

export class Moved extends TagComponent { }

export class IsBeingDragged extends TagComponent { }

//Stats
export class Health extends Component {
	hp: number = 0;
	lastHealStep = -1;

	reset() {
		this.hp = 0;
		this.lastHealStep = -1;
	}
}

export class Magnet extends Component {
	lastActivationStep = -1;
	force = 100;
	targets = new Array<Entity>();
	reset() {
		//this.color = AntigenColor.NoColor;
		this.lastActivationStep = -1;
		this.force = 100;
		this.targets = new Array<Entity>();
	}
}



export class Cooldown extends Component {
	cooldown = 0;
	remaining = 0;
	extra = 0;
	done = false;
	restarted = false;

	restart() {
		this.restarted = true;
	}

	reset() {
		this.cooldown = 0;
		this.remaining = 0;
		this.extra = 0;
		this.done = false;
		this.restarted = false;
	}

	getTimeLeft() {
		return this.remaining + this.extra;
	}
}

export class EffectRadius extends Component {
	radius = 0;
	reset() {
		this.radius = 0;
	}
}

export class VisibleRange extends Component {
	type = RangeType.Circle;
	range = 0;
	taggedRange = 0; //-1 means can't see tagged
	nearest: Entity | null = null;
	sticky = false;
	filter: CollisionCategory = CollisionCategory.All;
	forwardOffset = 0
	excludeList?: Set<number>;
	resetExcludeList?: boolean;
	excludeElements: GameElement[] | null = null;
	targetFound = false;
	visualize = true;

	reset() {
		this.type = RangeType.Circle;
		this.range = 0;
		this.taggedRange = 0;
		this.nearest = null;
		this.sticky = false;
		this.filter = CollisionCategory.All;
		this.forwardOffset = 0;
		this.excludeList = undefined;
		this.resetExcludeList = false;
		this.excludeElements = null;
		this.targetFound = false;
		this.visualize = true;
	}
}

//End Stats

//Stat related
export class VirusPointAttractor extends Component {
	attractorPoint = vec2.zero.copy();
	joinedEntities = new Set<Entity>();
	targetIdx = -1;
	disabled = false;

	reset() {
		this.attractorPoint = vec2.zero.copy();
		this.joinedEntities = new Set<Entity>();
		this.targetIdx = -1;
		this.disabled = false;
	}
}

export class CooldownReserve extends Component {
	reserve = 0;
	merge(other: CooldownReserve) {
		this.reserve += other.reserve;
	}
	reset() {
		this.reserve = 0;
	}
}

export class Buff extends Component implements MergableComponent<Buff> {
	buffs = new Array<{
		stat: keyof ElementStats,
		value: number,
		isPermanent?: boolean,
		max?: number,
		sourceEntity?: number,
	}>();

	merge(other: Buff) {
		this.buffs = this.buffs.concat(other.buffs);
	}

	reset() {
		this.buffs = [];
	}
}

export class TemporaryBuff extends Component {
	buffs = new Array<{
		stat: keyof ElementStats,
		value: number,
		applied?: boolean, //for when these buffs are stacked
	}>();

	merge(other: TemporaryBuff) {
		this.buffs = this.buffs.concat(other.buffs);
	}

	reset() {
		this.buffs = [];
	}
}

export class Buffed extends Component {
	lastBuffed: StatName | 'none' = 'none'; //one value here currently means can only show max one buff at once in UI
	lastBuffedValue = 0;
	lastBuffWasTemporary = false;
	lastBuffWasPermenent = false;
	lastBuffSources: Array<number> = [];

	health = 0;
	range = 0;
	cooldown = 0;
	speed = 0;
	sellValue = 0;
	effectRadius = 0;
	charge = 0;

	reset() {
		this.lastBuffed = 'none';
		this.lastBuffedValue = 0;
		this.lastBuffWasTemporary = false;
		this.lastBuffWasPermenent = false;
		this.health = 0;
		this.range = 0;
		this.cooldown = 0;
		this.speed = 0;
		this.sellValue = 0;
		this.effectRadius = 0;
		this.charge = 0;
		this.lastBuffSources = [];
	}
}

export class CopyBuffs extends Component {
	sourceId: number = -1;
	reset() {
		this.sourceId = -1;
	}
}

export class BuffPreview extends Component {
	stat: StatName | 'none' = 'none';
	isOutgoing = false;
	activeEmitter: any = null;
	reset() {
		this.stat = 'none';
		this.isOutgoing = false;
		this.activeEmitter = null;
	}
}

export class HealthSiphon extends Component {
	victim: Entity = Uninitialized;
	reset() {
		this.victim = Uninitialized;
	}
}

//end stat related

export class TimedLifespan extends Component {
	stepsToLive = 0;
	firstStep = 0

	reset() {
		this.stepsToLive = 0;
		this.firstStep = 0;
	}
}

export class ArenaLimit extends Component {
	maxDistance = 0;
	minDistance = 0;
	forcePerExcessDistance = 1;

	reset() {
		this.maxDistance = 0;
		this.minDistance = 0;
		this.forcePerExcessDistance = 1;
	}
}

export class Jump extends Component {
	progress = 0;
	isActive = false;
	activePeriod = 0;
	jumpForce = 0;

	reset() {
		this.progress = 0;
		this.isActive = false;
		this.activePeriod = 0;
		this.jumpForce = 0;
	}
}

export class ReplaceSelf extends Component {
	numSplits = 1;
	spawnTypeSource: 'self' | 'specified' | 'nearest' = 'self';
	spawnType: GameElement | null = null;
	initialPush = 0;
	spreadRadians = 0;

	reset() {
		this.numSplits = 1;
		this.spawnTypeSource = 'self';
		this.spawnType = null;
		this.initialPush = 0;
		this.spreadRadians = 0;
	}
}

export class HasSplit extends TagComponent { }


export class Shoot extends Component {
	aim = false;
	fireOnSight = false;
	scale = false;
	ammo: GameElement = GameElement.BULLET;
	shots: number = Infinity;
	shotsFired = 0;
	initialImpulse = 0;
	shotHealth = 1;

	reset() {
		this.aim = false;
		this.fireOnSight = false;
		this.scale = false;
		this.ammo = GameElement.BULLET;
		this.shots = Infinity;
		this.shotsFired = 0;
		this.initialImpulse = 0;
		this.shotHealth = 1;
	}
}

export class ShotPrice extends Component {
	amount = 0;
	reset() {
		this.amount = 0;
	}
}

export interface SpawnSpec {
	type: GameElement;
	promoted?: boolean;
	params?: EntityParameters;
	initialPush?: number;
	rotation?: number;
	headingRotation?: number;
	offset?: vec2;
}

export class Spawner extends Component {
	spawns: SpawnSpec[] = [];
	amount = 0;
	spawningNow = false;

	reset() {
		this.spawns = [];
		this.amount = 0;
		this.spawningNow = false;
	}
}

export class SpawnCounter extends SystemStateComponent {
	sprite: PIXI.Container | null = null;
	type: GameElement = GameElement.VIRUS;
	amount = -1; //diffed against spawner amount so can't start at 0

	reset() {
		this.sprite = null;
		this.type = GameElement.VIRUS;
		this.amount = -1;
	}
}

export class SpawnOnce extends Component implements MergableComponent<SpawnOnce> {
	spawns: SpawnSpec[] = [];
	reset() {
		this.spawns = [];
	}
	merge(other: SpawnOnce): void {
		this.spawns = this.spawns.concat(other.spawns);
	}
}

export class LaneInfo extends TagComponent {
	lane = -1;

	reset() {
		this.lane = -1;
	}
}


export class ScatterProtein extends Component {
	type: ConsumableType = ConsumableType.Bullet;
	fixedAmount = 0; //if 0 and Consumer, then use consumed amount
	spreadRadiens = 2 * Math.PI;
	targetViruses = false;
	range = 0;
	reset() {
		this.type = ConsumableType.Bullet;
		this.fixedAmount = 0;
		this.spreadRadiens = 2 * Math.PI;
		this.targetViruses = false;
		this.range = 0;
	}
}


//Rendering
export class Shape extends Component {
	primitive = ShapePrimative.HEX
	color = 0x222222;
	alpha = 1;
	borderWidth = 2;
	borderColor = 0xb74843;

	reset() {
		this.primitive = ShapePrimative.HEX;
		this.color = 0x222222;
		this.alpha = 1;
		this.borderWidth = 2;
		this.borderColor = 0xb74843;
	}
}

export class Renderable extends Component {
	sprite: PIXI.Sprite = Uninitialized;
	depthSprite: PIXI.Sprite = Uninitialized;
	glowSprite: PIXI.Sprite = Uninitialized;
	membrane: PIXI.Sprite | null = null; //TODO?
	highlight = HighlightType.None;
	addedScale = vec2.zero.copy();
	trailScale = 1;
	positionRadiusHistory = new Array<{ position: vec2, radius: number, rotation: number, time: number }>();
	assetLocation = { x: 0, y: 0, width: 64, height: 64 };
	depthAssetLocation = { x: 0, y: 0, width: 64, height: 64 };
	membraneAssetLocation = { x: 0, y: 0, width: 64, height: 64 };
	glowAssetLocation = { x: 0, y: 0, width: 1, height: 1 };
	hitstopSeconds = 0;
	hitstopCatchupPercent = 1;
	bypassHitstopThisFrame = false;

	reset() {
		this.sprite = Uninitialized;
		this.depthSprite = Uninitialized;
		this.glowSprite = Uninitialized;
		this.membrane = Uninitialized;
		this.highlight = HighlightType.None;
		this.addedScale = vec2.zero.copy();
		this.trailScale = 1;
		this.positionRadiusHistory = [];
		this.assetLocation = { x: 0, y: 0, width: 64, height: 64 };
		this.depthAssetLocation = { x: 0, y: 0, width: 64, height: 64 };
		this.membraneAssetLocation = { x: 0, y: 0, width: 64, height: 64 };
		this.glowAssetLocation = { x: 0, y: 0, width: 1, height: 1 };
		this.hitstopSeconds = 0;
		this.hitstopCatchupPercent = 1;
		this.bypassHitstopThisFrame = false;
	}
}

export class WaterTrail extends Component {
	trailSprite: PIXI.Sprite = Uninitialized;

	reset() {
		this.trailSprite = Uninitialized;
	}
}

export class Cilia extends Component {
	location: AssetLocation = Uninitialized;
	ciliaSprite: PIXI.Sprite = Uninitialized;
	reset() {
		this.location = Uninitialized;
		this.ciliaSprite = Uninitialized;
	}
}

export class HealthPopup extends Component {
	duration = RenderEffects.HealthPopupFadeSeconds;
	lastTimeChanged = 0;
	sprite: PIXI.Container = Uninitialized;

	reset() {
		this.duration = RenderEffects.HealthPopupFadeSeconds;
		this.lastTimeChanged = 0;
		this.sprite = Uninitialized;
	}
}

export class ChargePopup extends Component {
	duration = RenderEffects.HealthPopupFadeSeconds; //eh just use same one
	lastTimeChanged = 0;
	sprite: PIXI.Container = Uninitialized;

	reset() {
		this.duration = RenderEffects.HealthPopupFadeSeconds;
		this.lastTimeChanged = 0;
		this.sprite = Uninitialized;
	}
}

//Types
export class Virus extends TagComponent { }

export class Antibody extends TagComponent {
}

export class Cytokine extends TagComponent {
}

export class BloodCell extends TagComponent {
}

export class Infected extends Component {
	targetPlayerIds: number[] = [];
	reset() {
		this.targetPlayerIds = [];
	}
}

export class InfectionGrowth extends Component {
	wave: number = -1;
	reset() {
		this.wave = -1;
	}
}

export class VirusLoadIncrease extends TagComponent { }

//Behaviors
export class TaggedVirus extends Component {
	bulletTargetingId = -1
	reset() {
		this.bulletTargetingId = -1;
	}
}

export class TagProof extends TagComponent { }

//Targeting

export enum RangeType {
	Circle,
	Line,
	Cone
}


//Not a component, a member class of the targeting component
export class TargetingSpec {
	constructor(
		public type = RangeType.Circle,
		public range = 50,
		public filter: CollisionCategory = CollisionCategory.All,
		public taggedRange = 50,
		public nearestOnly = false,
		public sticky = false,
		public forwardOffset = 0,
		public disable = false,
		public excludeElements: GameElement[] | null = null,
	) { }
}

/**Specify a description of targets with addSpec, and on following frames retrieve results with getTargets() using assigned index.
 * For instantaneous targeting use queryRadius other utility functions, but be warned performance may suffer calling those every frame.
 */
export class Targeting extends Component {
	specs: Array<TargetingSpec> = [];
	results: Array<Array<Entity>> = [];

	reset() {
		this.specs = [];
		this.results = [];
	}

	addSpec(type: RangeType, range: number, filter: CollisionCategory, taggedRange = 0, nearestOnly = false, sticky = false, forwardOffset = 0) {
		const len = this.specs.push(new TargetingSpec(type, range, filter, taggedRange, nearestOnly, sticky, forwardOffset));
		return len - 1;
	}

	disableSpec(specIndex: number) {
		this.specs[specIndex].disable = true;
	}

	enableSpec(specIndex: number) {
		this.specs[specIndex].disable = false;
	}

	getTargets(targetResultIndex: number) {
		return this.results[targetResultIndex] || [];
	}

}

export class TargetAcquiredThisFrame extends TagComponent { }

export class ReturningHome extends TagComponent { }

export enum NoTargetBehavior {
	Drift,
	StayStill,
	ReturnHome,
}

//Direction
export class AimForTarget extends Component {
	target: Entity | null = null;
	leadTarget = true;
	noTargetBehavior: NoTargetBehavior = NoTargetBehavior.Drift;
	homePosition: vec2 | null = null;
	returnHomeTimer = BehaviorsConstants.SeekerReturnHomeTimeout;

	reset() {
		this.target = null;
		this.leadTarget = true;
		this.noTargetBehavior = NoTargetBehavior.Drift;
		this.homePosition = null;
		this.returnHomeTimer = BehaviorsConstants.SeekerReturnHomeTimeout;
	}
}

export class AimOrbit extends Component {
	orbitOwner = false;
	target = vec2.zero.copy();
	distance: number = Uninitialized;

	reset() {
		this.orbitOwner = false;
		this.target = vec2.zero.copy();
		this.distance = Uninitialized;
	}
}

export class AimForPosition extends Component {
	target = vec2.zero.copy();
	moveTowardsTarget = true;
	error = 0;

	reset() {
		this.target = vec2.zero.copy();
		this.moveTowardsTarget = true;
		this.error = 0;
	}
}

export class AimForDirection extends Component {
	fixedDirection: vec2 | null = null;
	reset() {
		this.fixedDirection = null;
	}
}

export class AimForPlayer extends Component {
	playerId = -1;
	onlyTargetOnce = false;
	disableAfterOneAim = false;
	target: Entity | null = null;

	reset() {
		this.playerId = -1;
		this.onlyTargetOnce = false;
		this.disableAfterOneAim = false;
		this.target = null;
	}
}

export class LowPriorityTarget extends Component {
	category: CollisionCategory = CollisionCategory.None;
	element: GameElement | -1 = -1;
	consumerOf: ConsumableType | -1 = -1;
	specIndex = 0;
	reset() {
		this.category = CollisionCategory.None;
		this.element = -1;
		this.consumerOf = -1;
		this.specIndex = 0;
	}
}



//Movement
export class AvoidMotion extends Component {
	force = 0;
	antibodyTargetingIndex = 0;

	reset() {
		this.force = 0;
		this.antibodyTargetingIndex = 0;
	}
}

export class OrientationControl extends Component {
	tolerance = 0.00872665; // 2 degrees in radians
	turnSpeed = 2

	reset() {
		this.tolerance = 0.00872665; // 2 degrees in radians
		this.turnSpeed = 2;
	}
}

//Like OrientationControl, but ramps towards the target via bounded torque (scaled proportionally to how far
//off the current angle is) instead of hard-snapping angular velocity.
export class TorqueOrientationControl extends Component {
	turnSpeed = 2;
	/**How much angular velocity should change in 1 second, ramping towards turnSpeed instead of snapping to it */
	turnAccel = 60;
	fixedDirection = vec2.zero.copy();

	reset() {
		this.turnSpeed = 2;
		this.turnAccel = 60;
		this.fixedDirection = vec2.zero.copy();
	}
}

export class Sentry extends AimOrbit {
	direction = vec2.zero.copy();
	maxSpeed = Speeds.Normal;

	reset() {
		super.reset();
		this.direction = vec2.zero.copy();
		this.maxSpeed = Speeds.Normal;
	}
}

export class SprinklerMotion extends Component {
	turnSpeed = 3;
	direction = 1;
	startingRotation = 0;
	maxAngle = Math.PI / 4;
	turningInProgress = false;

	reset() {
		this.turnSpeed = 3;
		this.direction = 1;
		this.startingRotation = 0;
		this.maxAngle = Math.PI / 4;
		this.turningInProgress = false;
	}
}

export class ContinuousMotion extends Component {
	/**Maximum speed to voluntarily accerlate towards */
	maxSpeed = 0;
	/**How much speed should change in 1 second (almost, doesn't include drag) */
	accel = 16;

	reset() {
		this.maxSpeed = 0;
		this.accel = 16;
	}
}

export class SineWaveMotion extends Component {
	period = 1;
	accel = Speeds.Fast;
	slope = 5;
	progress = 0; // must start midway through one end of wave

	reset() {
		this.period = 1;
		this.accel = Speeds.Fast;
		this.slope = 5;
		this.progress = 0;
	}
}

export class ImpulseMotion extends Component {
	force = 0;
	canRepeatOnCooldown = false;
	amount = Infinity;

	reset() {
		this.force = 0;
		this.canRepeatOnCooldown = false;
		this.amount = Infinity;
	}
}

export class UnstoppableMotion extends Component {
	speed = 0;
	reset() {
		this.speed = 0;
	}
}

export class InstantDamage extends Component {
	damage = 0;
	damagerId = -1;
	damageType = DamageType.Electricity;
	reset() {
		this.damagerId = -1;
		this.damage = 0;
		this.damageType = DamageType.Electricity;
	}
}

//State related
export class Dead extends Component {
	killerId = null
	killerType = GameElement.VIRUS;

	reset() {
		this.killerId = null
		this.killerType = GameElement.VIRUS;
	}
}

export class Healed extends TagComponent { }

//Conditional effects
export class ConditionalEffect extends Component {
	effect: FrozenComponent = Uninitialized;
	targetSelector: SimpleSelector = { type: 0 }; //strange server ecsy crash if I use TargetType enum here??
	filters: Array<TargetFilter> = [];

	reset() {
		this.effect = Uninitialized;
		this.targetSelector = { type: 0 }
		this.filters = [];
	}
}

export class HasConditionalEffect extends Component {
	effects: Array<new () => Component> = [];
	reset() {
		this.effects = [];
	}
}

//Triggers

export class Triggered extends TagComponent { }

export class OnDeath extends Component implements MergableComponent<OnDeath> {
	effect: TriggeredEffect | TriggeredEffect[] = Uninitialized;

	reset() {
		this.effect = Uninitialized;
	}

	merge(other: OnDeath) {
		let effects = [];
		if (this.effect instanceof Array) {
			effects = this.effect;
		}
		else {
			effects.push(this.effect);
		}
		if (other.effect instanceof Array) {
			effects = effects.concat(other.effect);
		}
		else {
			effects.push(other.effect);
		}
		this.effect = effects;
	}
}

export class OnSurvive extends Component {
	effect: TriggeredEffect = Uninitialized;

	reset() {
		this.effect = Uninitialized;
	}
}

export class OnBuyOther extends Component {
	effect: TriggeredEffect = Uninitialized;
	typeFilter: GameElement = GameElement.SHOPABLE_ELEMENT_NUMBER;
	categoryFilter: CellCategory = CellCategory.None;

	reset() {
		this.effect = Uninitialized;
		this.categoryFilter = CellCategory.None;
		this.typeFilter = GameElement.SHOPABLE_ELEMENT_NUMBER;
	}
}

export class OnBuff extends Component {
	stat: StatName = 'health';
	effect: TriggeredEffect = Uninitialized;

	reset() {
		this.stat = 'health'
		this.effect = Uninitialized;
	}
}

export class HealBooster extends Component {
	amount = 0;
	categoryFilter: CellCategory = CellCategory.None;
	reset() {
		this.amount = 0;
		this.categoryFilter = CellCategory.None;
	}
}

export class OnDamage extends Component {
	effect: TriggeredEffect | TriggeredEffect[] = Uninitialized;

	reset() {
		this.effect = Uninitialized;
	}
}

export class OnKill extends Component {
	effect: TriggeredEffect = Uninitialized;

	reset() {
		this.effect = Uninitialized;
	}
}

export class OnBuy extends Component {
	effect: TriggeredEffect = Uninitialized;

	reset() {
		this.effect = Uninitialized;
	}
}

export class OnStartBattle extends Component {
	effect: TriggeredEffect = Uninitialized;

	reset() {
		this.effect = Uninitialized;
	}
}

export class OnStartShop extends Component {
	effect: TriggeredEffect = Uninitialized;

	reset() {
		this.effect = Uninitialized;
	}
}

export class OnMoveDistance extends Component {
	effect: TriggeredEffect = Uninitialized;
	distance: number = 10;
	progress: number = 0;

	reset() {
		this.effect = Uninitialized;
		this.distance = 10;
		this.progress = 0;
	}
}

export class DragDropProtein extends Component {
	proteinType = GameElement.INTERFERON;
	distance: number = 10;
	progress: number = 0;
	lastPosition: vec2 | null = null;
	triggers = Infinity;

	reset() {
		this.proteinType = GameElement.INTERFERON;
		this.distance = 10;
		this.progress = 0;
		this.lastPosition = null;
		this.triggers = Infinity;
	}
}

export class OnConsume extends Component {
	effect: TriggeredEffect | TriggeredEffect[] = Uninitialized;
	requiredAmount: Array<{ type: ConsumableType, amount: number }> = [];
	cooldown = 0;
	currentCooldown = 0;

	reset() {
		this.effect = Uninitialized;
		this.requiredAmount = [];
		this.cooldown = 0;
		this.currentCooldown = 0;
	}
}

export class OnNearTarget extends Component {
	effect: TriggeredEffect | TriggeredEffect[] = Uninitialized;
	currentDistance = Infinity;
	thresholdDistance = Infinity;

	reset() {
		this.effect = Uninitialized;
		this.currentDistance = Infinity;
		this.thresholdDistance = Infinity;
	}
}

export class OnCollision extends Component {
	effect: TriggeredEffect | TriggeredEffect[] = Uninitialized;
	componentFilter = new Array<new () => Component>();
	cooldown = 0;
	perEntityCooldown = 0;
	currentCooldown = 0;
	currentPerEntityCooldown = new Map<Entity, number>();

	reset() {
		this.effect = Uninitialized;
		this.componentFilter = [];
		this.cooldown = 0;
		this.perEntityCooldown = 0;
		this.currentCooldown = 0;
		this.currentPerEntityCooldown = new Map<Entity, number>();
	}
}

export class AoE extends Component {
	currentRadius = 0;
	radiusPerSecond = 0;
	durationRemaining = 0;
	force = 0;
	targetFilter = CollisionCategory.All;
	entityFilter = new Set<number>();
	hitOnce = true;
	alreadyHit = new Set<number>();
	targetingIndex = 0;

	reset() {
		this.currentRadius = 0;
		this.radiusPerSecond = 0;
		this.durationRemaining = 0;
		this.force = 0;
		this.targetFilter = CollisionCategory.All;
		this.entityFilter = new Set<number>();
		this.hitOnce = true;
		this.alreadyHit = new Set<number>();
		this.targetingIndex = 0;
	}
}

export class Bullet extends Component {
	creatorSyncId: number | undefined = undefined;
	taggedTargetVirus: Entity | null = null;

	reset() {
		this.creatorSyncId = undefined;
		this.taggedTargetVirus = null;
	}
};

export enum ConsumableType {
	CapsidProtein,
	Interferon,
	Bullet
}

export class Consumable extends Component {
	type: ConsumableType = ConsumableType.CapsidProtein;
	consumed = false;
	reset() {
		this.type = ConsumableType.CapsidProtein;
		this.consumed = false;
	}
}

export class Consumer extends Component {
	range = 25;
	types = new Array<ConsumableType>();
	targets = new Array<Entity>();
	consumedAmounts = new Array<number>(0, 0, 0);

	reset() {
		this.range = 25;
		this.types = [];
		this.targets = [];
		this.consumedAmounts = [0, 0, 0];
	}
}

export class NonPhysicsProtein extends TagComponent { }

export class Eat extends Component {
	max = BehaviorsConstants.MaxVirophageSize;
	current = 0;
	gravStrength = 1;

	reset() {
		this.max = BehaviorsConstants.MaxVirophageSize;
		this.current = 0;
		this.gravStrength = 1;
	}
}

export class Grow extends TagComponent { }

export class Submerged extends Component {
	surfacing = false;
	rate = 0.4;
	percentSubmerged = 0;
	collisionMask = CollisionCategory.None;
	max = 1;
	savedMaskBits = 0;
	//This effect barely work currently, this is a hack to make infected cells not fully disappear like blood cells
	dontAffectScale = false;

	reset() {
		this.surfacing = false;
		this.rate = 0.4;
		this.percentSubmerged = 0;
		this.collisionMask = CollisionCategory.None;
		this.max = 1;
		this.savedMaskBits = 0;
		this.dontAffectScale = false;
	}
}

//Money related
export class SetHealthEqualMoney extends Component {
	convertionRate = 1;
	reset() {
		this.convertionRate = 1;
	}
}

export class GainMoney extends Component implements MergableComponent<GainMoney> {
	amount = 0;
	merge(other: GainMoney) {
		this.amount += other.amount;
	}
	reset() {
		this.amount = 0;
	}
}

export class GainInterest extends Component {
	amount = 1;
	requiredSavings = 1
	reset() {
		this.amount = 1;
		this.requiredSavings = 1;
	}
}

export class SpendMoney extends Component implements MergableComponent<SpendMoney> {
	amount = 0;
	merge(other: SpendMoney) {
		this.amount += other.amount;
	}
	reset() {
		this.amount = 0;
	}
}

//Player association
export class Ownership extends Component {
	playerId: number = Uninitialized;
	sourceCompElement: CompositionElement | null = null //TODO: Get this out of here, it is a huge pain to deal with

	reset() {
		this.playerId = Uninitialized;
		this.sourceCompElement = null;
	}
}

//client-server
export class Sync extends Component {
	id: number = Uninitialized;
	type: GameElement = Uninitialized;
	rng: Rng = Uninitialized;

	reset() {
		this.id = Uninitialized;
		this.type = Uninitialized;
		this.rng = Uninitialized;
	}
}

export class PreviewOnly extends Component {
	onEndPreview: ((isValid: boolean) => void) | null = null; reset() {
		this.onEndPreview = null;
	}
};

export class Placeable extends TagComponent { };
export class Hoverable extends TagComponent { };

export class OnScreen extends TagComponent { };
export class JustOffScreen extends TagComponent { };
export class JustCombined extends Component {
	combinedPoints = 0;
	reset() {
		this.combinedPoints = 0;
	}
};
export class Flashing extends Component {
	color = 0xeeeeee;
	lengthSec = 0.5;
	progress = 0;
	times = -1;

	reset() {
		this.color = 0xeeeeee;
		this.lengthSec = 0.5;
		this.progress = 0;
		this.times = -1;
	}
}

export class Inflating extends Component {
	inflation = 0;
	reset() {
		this.inflation = 0;
	}
}

export class Pop extends Component {
	stretchPercent = new vec2();
	durationMS = 1;
	delayMS = 0;
	reset() {
		this.stretchPercent.reset();
		this.durationMS = 1;
		this.delayMS = 0;
	}
}

export class ShowRangeBuff extends Component {
	remaining = 0;
	reset() {
		this.remaining = 0;
	}
}

export class ShowStat extends Component {
	stats = new Set<StatName>();
	remainingMS = 0;
	reset() {
		this.stats = new Set<StatName>();
		this.remainingMS = 0;
	}
}

//sounds
export class SoundSource extends Component {
	sound: Sound = Uninitialized;

	reset() {
		this.sound = Uninitialized;
	}
}

export class Invisible extends TagComponent { };
export class Promoted extends TagComponent { };


export class HexTileable extends Component {
	tiledSides: [number, number, number, number, number, number] = [0, 0, 0, 0, 0, 0];
	size = cellLocations.bloodcell.width; //assuming this only blood cells right now
	adjacent = new Array<Entity | undefined>(6);
	buffer: Float32Array | null = null;
	reset() {
		this.tiledSides = [0, 0, 0, 0, 0, 0];
		this.size = cellLocations.bloodcell.width; //assuming this only blood cells right now;
		this.adjacent = new Array<Entity | undefined>(6);
		this.buffer = null;
	}

	computeTilingIndex() {
		let index = 0;
		for (let i = 0; i < this.tiledSides.length; ++i) {
			const side = this.tiledSides[i];
			index = index | (side << i);
		}
		return index;
	}
}

export class SpriteTrackingParticles extends Component {
	///@ts-ignore
	emitters: Array<any> = [];
	container: PIXI.Container = Uninitialized;
	reset() {
		this.emitters = [];
		this.container = Uninitialized;
	}
}

//Electricity
export class ElectricCharge implements MergableComponent<ElectricCharge> {
	charge = 0;
	reset() {
		this.charge = 0;
	}
	merge(other: ElectricCharge) {
		this.charge += other.charge;
	}
}

export interface UnitOfElectricCurrent {
	source: Entity;
	pathTraveled: Array<Entity>; //TODO: ughhh
}

export class ElectricConductor extends Component {
	accessCharge = false;
	liveCurrent = new Set<UnitOfElectricCurrent>();
	outgoing: Set<Entity> = new Set();
	incoming: Set<Entity> = new Set();
	targets = new Array<Entity>();

	reset() {
		this.accessCharge = false;
		this.liveCurrent = new Set();
		this.outgoing = new Set();
		this.incoming = new Set();
		this.targets = [];
	}
}

export class ConvertChargeToEffectRadius extends Component {
	conversionRate = 1;
	reset() {
		this.conversionRate = 1;
	}
}

export class ElectricSink extends TagComponent { }
